import * as React from 'react';
import {
  FormattedMessage as OrigFormattedMessage,
  IntlProvider,
} from 'react-intl';
import type {ElementConfig} from '@pinecast/common/types';

import {locale as buildLocale} from './locale';
import {defaultRichTextElements} from './richText';

export type {IntlShape} from 'react-intl';
export {defineMessage, useIntl} from 'react-intl';

export const Provider = ({
  children,
  ...rest
}: Partial<ElementConfig<typeof IntlProvider>>) => (
  <IntlProvider
    locale={buildLocale}
    // A locale bundle's `defaultMessage`s *are* that locale — the build
    // translated them in place — so `defaultLocale` has to follow `locale` or
    // react-intl formats their plurals and numbers under English rules and logs
    // a missing-translation error for every single one.
    defaultLocale={buildLocale}
    defaultRichTextElements={defaultRichTextElements}
    {...rest}
  >
    {children}
  </IntlProvider>
);

type ValueType =
  | React.ReactNode
  | ((chunks: React.ReactNode) => React.ReactNode);

interface ExtendedProps {
  id?: string;
  description?: string;
  defaultMessage: string;
  values?: Record<string, ValueType>;
  className?: string;
  style?: React.CSSProperties;
}

export const FormattedMessage = ({className, style, ...rest}: ExtendedProps) =>
  style || className ? (
    <OrigFormattedMessage {...rest}>
      {content => (
        <span className={className} style={style}>
          {content}
        </span>
      )}
    </OrigFormattedMessage>
  ) : (
    <OrigFormattedMessage {...rest} />
  );

// `id` is assigned at build time by @formatjs/ts-transformer (see
// ts-loader.options.js), hashed from `defaultMessage` + `description`. Source
// never spells one out: two descriptors with identical text and description are
// deliberately the same translation unit, so `description` is what disambiguates
// strings that must translate differently.
export type Message = {
  id?: string;
  description: string;
  defaultMessage: string;
};
